<template>
  <basic-card>
    <template slot="header">{{$t('users.detailName')}} {{ user.username }}</template>
    <form-wrapper :form-title="`${$t('users.detailName')}`">
      <form-text-input v-model="user.id" caption="Id" disabled/>
      <form-text-input help-item-code="user-username" v-model="user.username" :caption="`${$t('users.username')}`"/>
      <form-text-input v-model="user.email" :caption="`${$t('users.email')}`"/>
      <form-text-input v-if="user.id == undefined" v-model="user.password" :caption="`${$t('users.password')}`"/>
      <form-select-input v-model="user.role" :caption="`${$t('users.role')}`" :options="roles"/>
      <form-select-input :options="securityGroups" v-model="user.defaultSecurityGroup"
                         :caption="`${$t('users.defaultSecGroup')}`" help-item-code="user-default-security-group"/>
      <form-checkbox-input v-model="user.blocked" :caption="`${$t('users.blocked')}`"/>
      <form-text-input v-model="user.blockedReason" :caption="`${$t('users.reasonBlocked')}`"/>
    </form-wrapper>
    <framed-content :title="`${$t('users.secGroupAccesses')}`">
      <table>
        <thead>
        <tr>
          <th rowspan="2">{{$t('securityGroup')}}</th>
          <th colspan="4">
            {{$t('objects')}}
          </th>
          <th rowspan="2">{{ $t('dataTable.actions') }}</th>
        </tr>
        <tr>
          <th>{{$t('sidebar.banking')}}</th>
          <th>{{$t('sidebar.operations')}}</th>
          <th>{{$t('cashflow.cashEvents')}}</th>
          <th>{{$t('banking.reconciliation')}}</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="securityGroupAccess in user.securityGroupAccesses">
          <td>{{ securityGroups.find(secGrp => secGrp.id == securityGroupAccess.securityGroup).name }}</td>
          <td :class="{[securityGroupAccess.groupAccessLevels.account]: true}"
              @click="toggleAccessLevel(securityGroupAccess.groupAccessLevels, 'account')">
            {{ securityGroupAccess.groupAccessLevels.account }}
          </td>
          <td :class="{[securityGroupAccess.groupAccessLevels.operation]: true}"
              @click="toggleAccessLevel(securityGroupAccess.groupAccessLevels, 'operation')">
            {{ securityGroupAccess.groupAccessLevels.operation }}
          </td>
          <td :class="{[securityGroupAccess.groupAccessLevels.cashEvent]: true}"
              @click="toggleAccessLevel(securityGroupAccess.groupAccessLevels, 'cashEvent')">
            {{ securityGroupAccess.groupAccessLevels.cashEvent }}
          </td>
          <td :class="{[securityGroupAccess.groupAccessLevels.reconciliationMatch]: true}"
              @click="toggleAccessLevel(securityGroupAccess.groupAccessLevels, 'reconciliationMatch')">
            {{ securityGroupAccess.groupAccessLevels.reconciliationMatch }}
          </td>
          <td>
            <div style="display: flex; flex-flow: row; grid-gap: 5px;">
              <simple-button @press="changeAccesses('READ', securityGroupAccess)">{{ $t('users.allRead') }}</simple-button>
              <simple-button @press="changeAccesses('WRITE', securityGroupAccess)">{{ $t('users.allWrite') }}</simple-button>
              <simple-button @press="changeAccesses('FORBIDDEN', securityGroupAccess)">{{ $t('users.allForbidden') }}</simple-button>
            </div>
          </td>
        </tr>
        </tbody>
      </table>
    </framed-content>

      <simple-button @press="promptResetPassword">{{$t('users.resetPassword')}}</simple-button>
    <br/>
      <simple-button confirmation-protected :disabled="actualUser.role !== 'Manager'"
                     :protection-message="`${$t('users.savingUser')} ${user.username}`"
                     @proceed="handleSave">
          {{ $t('save') }}
      </simple-button>

    <v-modal name="resetPassModal" classes="transparent-modal" style="background-color: rgba(10,10,10,0.66)"
             :width="800" :height="600">
      <basic-card>
        <framed-content title="Password reset">
          <form-wrapper>
            <form-text-input v-model="passwordResetDto.oldPassword" caption="Old password" password
                             v-if="actualUser.role != 'Manager'"/>
            <form-text-input v-model="passwordResetDto.newPassword" caption="New password" password/>
          </form-wrapper>
        </framed-content>
        <div style="display: flex; flex-flow: row; justify-content: end; gap: 1em;">
          <simple-button @press="cancelResetPassword">{{$t("cancel")}}</simple-button>
          <simple-button @press="processResetPassword">Reset password</simple-button>
        </div>
      </basic-card>
    </v-modal>
  </basic-card>
</template>

<script>
import BasicCard from "../components/layout/BasicCard";
import FormTextInput from "../components/form/FormTextInput";
import FormWrapper from "../components/form/FormWrapper";
import FormSelectInput from "../components/form/FormSelectInput";
import SimpleButton from "../components/button/SimpleButton";
import FormCheckboxInput from "../components/form/FormCheckboxInput";
import {mapGetters} from "vuex";
import Frame from "../components/layout/FramedContent";
import FramedContent from "../components/layout/FramedContent";
import InlineSwitch from "../components/button/InlineSwitch";
import Axios from 'axios'

export default {
  name: "UserFormCard",
  components: {
    InlineSwitch,
    FramedContent,
    Frame, FormCheckboxInput, SimpleButton, FormSelectInput, FormWrapper, FormTextInput, BasicCard
  },
  props: {
    user: Object,
  },
  data: function () {
    return {
      passwordResetDto: {
        oldPassword: "",
        newPassword: ""
      }
    }
  },
  computed: {
    ...mapGetters({
      "securityGroups": "securityGroups/securityGroups",
      'actualUser': 'auth/actualPrincipal'
    }),
    securityGroupAccessLevels() {
      return [
        {
          id: 'FORBIDDEN',
          name: 'FORBIDDEN'
        },
        {
          id: 'READ',
          name: 'READ'
        },
        {
          id: 'WRITE',
          name: 'WRITE'
        }
      ]
    },
    roles() {
      return [{id: "Operator", name: this.$t('users.operator')}, {id: "Manager", name: this.$t('users.manager')}]
    }
  },
  methods: {
    cancelResetPassword() {
      this.passwordResetDto.oldPassword = "";
      this.passwordResetDto.newPassword = "";
      this.$modal.hide("resetPassModal");
    },
    async processResetPassword() {
      try {
        await Axios.post(`auth/api/v1/users/${this.user.id}/reset-password`, this.passwordResetDto);
        this.cancelResetPassword();
        this.$notify({group: 'application', type: "info", text:  this.$t('users.resetSucces'), title: "Password reset"})
      }
      catch(e) {
        this.cancelResetPassword();
        this.$notify({group: 'application', type: "error", text: "Failed to reset password, you don't have enough permissions or you entered wrong old password", title: "Password reset failure"})
      }
    },
    promptResetPassword() {
      this.$modal.show("resetPassModal");
    },
    changeAccesses(level, groupAccess) {

      groupAccess.groupAccessLevels.account = level;
      groupAccess.groupAccessLevels.operation = level;
      groupAccess.groupAccessLevels.cashEvent = level;
      groupAccess.groupAccessLevels.reconciliationMatch = level;
    },
    adjustSecurityGroupAccesses() {
      this.securityGroups.forEach(secGroup => {
        const forbiddenAll = {
          securityGroup: secGroup.id,
          groupAccessLevels: {
            account: "FORBIDDEN",
            cashEvent: "FORBIDDEN",
            operation: "FORBIDDEN",
            reconciliationMatch: "FORBIDDEN"
          }
        }
        let currentIdx = this.user.securityGroupAccesses.findIndex(sga => sga.securityGroup == forbiddenAll.securityGroup);
        if (currentIdx >= 0) {
          this.user.securityGroupAccesses[currentIdx] = Object.assign(forbiddenAll, this.user.securityGroupAccesses[currentIdx]);
        } else {
          this.user.securityGroupAccesses.push(forbiddenAll);
        }
      }, {})
    },
    toggleAccessLevel(accessLevels, object) {
      if (accessLevels[object] == 'READ') accessLevels[object] = "WRITE";
      else if (accessLevels[object] == 'WRITE') accessLevels[object] = "FORBIDDEN";
      else if (accessLevels[object] == "FORBIDDEN") accessLevels[object] = "READ";
    },
    handleSave() {
      return this.$emit("save", this.user);
    },
  },
  mounted() {
    this.adjustSecurityGroupAccesses();
  }
}
</script>

<style lang="scss" scoped>
@import "../styles/global";

.FORBIDDEN {
  background-color: $backgroundColor;
}

.READ {
  background-color: lighten($backgroundColor, 10);
}

.WRITE {
  background-color: lighten($backgroundColor, 15);
}

table {
  border-collapse: collapse;

  td, th {
    padding: 5px;
    border: 1px solid lighten($backgroundColor, 20);
    min-width: 100px
  }

  td {
    cursor: pointer;
  }

  td:first-child, th {
    background-color: lighten($backgroundColor, 20);
    border: 1px solid lighten($backgroundColor, 10);
  }
}

</style>
